import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../../../reducers';
import {FeedbackType, FeedbackStatus} from '../../../dto/FeedbackDto';
import {RelationType} from '../../../dto/RelationDto';

export const qualityFeedback = (state: RootState) => state.qualityFeedback;

export const sortProjectsByName = (state: RootState) => {
    const mainProjectId = state.auth.profile?.mainProjectId;
    const mainProject = state.qualityFeedback.projects.filter((el) => el.projectId === mainProjectId);
    const otherProjects = state.qualityFeedback.projects
        .filter((el) => el.projectId !== mainProjectId)
        .sort((a, b) => a.projectName.localeCompare(b.projectName));
    return [...mainProject, ...otherProjects];
};

export const feedbacksByProjectId = (projectId: number, periodIslocked: boolean) =>
    createSelector(qualityFeedback, (qualityState) => {
        const projectWithTeam = qualityState.profiles.find((el) => el.projectId === projectId);
        const feedbacks = qualityState.feedbacks.find((el) => el.projectId === projectId)?.feedbacks;
        if (projectWithTeam && feedbacks) {
            const feedbacksWithProfile = projectWithTeam.team
                .map(({profile, relationType, relationId}) => {
                    const {avatarURL, firstName, lastName, position, id} = profile;
                    const feedback = feedbacks.find(({recipientId}) => recipientId === id);
                    if (feedback) {
                        return {avatarURL, firstName, lastName, position, relationId, ...feedback};
                    } else {
                        return {
                            avatarURL,
                            firstName,
                            lastName,
                            position,
                            relationId,
                            feedbackId: null,
                            feedbackStatus: FeedbackStatus.SAVED,
                            feedbackType:
                                relationType === RelationType.LEAD ? FeedbackType.REQUIRED : FeedbackType.OPTIONAL,
                            recipientId: id,
                            message: '',
                            reasons: []
                        };
                    }
                })
                .filter((el) => (periodIslocked ? el.feedbackStatus === FeedbackStatus.SUBMITTED : true))
                .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName));

            return {
                requiredFeedbacks: feedbacksWithProfile.filter((el) => el.feedbackType === FeedbackType.REQUIRED),
                optionalFeedbacks: feedbacksWithProfile.filter((el) => el.feedbackType === FeedbackType.OPTIONAL),
                positions: Array.from(new Set(feedbacksWithProfile.map((el) => el.position)))
            };
        }
        return {requiredFeedbacks: [], optionalFeedbacks: [], positions: []};
    });

export const templatesByProjectId = (projectId: number) =>
    createSelector(qualityFeedback, (qualityState) =>
        qualityState.templates.filter((template) => template.projectId === projectId)
    );

export const getProjectById = (projectId: number) =>
    createSelector(qualityFeedback, (qualityState) => qualityState.projects.find((el) => el.projectId === projectId));

export const getProfilesWithProjects = createSelector(qualityFeedback, (qualityState) => {
    const {isFetching} = qualityState;
    const profilesWithProjects = qualityState.profilesWithProjects
        .map(({profile, projectsInfo}) => {
            const {mainProjectId} = profile;
            const mainProject = projectsInfo.filter(({projectId}) => projectId === mainProjectId);
            const otherProjects = projectsInfo
                .filter(({projectId}) => projectId !== mainProjectId)
                .sort((a, b) => a.projectName.localeCompare(b.projectName));
            return {profile, projectsInfo: [...mainProject, ...otherProjects]};
        })
        .filter((el) => el.projectsInfo.length)
        .sort((a, b) =>
            (a.profile.firstName + a.profile.lastName).localeCompare(b.profile.firstName + b.profile.lastName)
        );

    return {isFetching, profilesWithProjects};
});

export const getViewFeedbacksCount = (projectId: number, recipientId: number) =>
    createSelector(qualityFeedback, (qualityState) => {
        const feedbacks = qualityState.lastView.filter(
            (el) => el.projectId === projectId && el.recipientId === recipientId
        );

        return {
            count: feedbacks.length,
            hasNew: feedbacks.some((el) => el.new),
            feedbackIds: feedbacks.map((el) => el.feedbackId),
            profileIds: Array.from(new Set(feedbacks.map((el) => el.authorId)))
        };
    });

export const lastViewFeedbacks = (projectId: number, recipientId: number) =>
    createSelector(qualityFeedback, (qualityState) => {
        const feedbacks = qualityState.lastViewFeedbacks.filter(
            (el) => el.projectId === projectId && el.recipientId === recipientId
        );

        return feedbacks.map(({authorId, message, reasons, feedbackId}) => {
            const author = qualityState.lastViewProfiles.find((el) => el.id === authorId);
            const rating = reasons.reduce((acc, cur) => acc + cur.coefficient, 1);
            const lastView = qualityState.lastView.find((el) => el.feedbackId === feedbackId);
            const isNew = lastView?.new as boolean;
            const viewId = lastView?.id as number;

            return {author, message, rating, isNew, viewId};
        });
    });
